import React from 'react';

import { Grid, Section, normalizeSelectValue, Alert } from '@gogame/components';
import { FormField, withForm, SidePageV2 } from '@gogame/form';
import { useDateFormat } from '@gogame/intl-ts';

import ConceptFamiliesMultiSelect from 'scenes/Gallery/GalleryPage/Concepts/components/ConceptFamiliesMultiSelect';
import StatusSchedulesField from 'scenes/Gallery/GalleryPage/Concepts/components/StatusSchedulesField';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/constants';
import {
  loadApplications,
  loadPromoCodes,
  loadRequestedBy,
  loadTags,
} from 'scenes/Gallery/GalleryPage/Concepts/sources/fields';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import {
  usePromoCodeDetails,
  useUploadAttachments,
} from 'scenes/Gallery/GalleryPage/Concepts/data';
import TagsMultiSelect from 'scenes/GalleryTags/components/TagsMultiSelect';

const EditConceptForm = withForm({
  enableReinitialize: true,
})(({ children, values, loading, onClose, open, onExited, entityId }) => {
  const {
    isAllowJiraTask,
    isAllowConceptFamilies,
    isAllowRequestedBy,
    isAllowPromoCodes,
    isAllowChangeConceptStatus,
    isAllowAttachments,
    isAllowAddTags,
  } = useGalleryPermissions();

  const dateFormat = useDateFormat({
    template: 'LL',
  });
  const { data: promoCodeDetails } = usePromoCodeDetails(
    {
      id: values[CONCEPT.PROMO_CODE],
    },
    {
      enabled: !!values[CONCEPT.PROMO_CODE],
    }
  );
  const uploadAttachments = useUploadAttachments();

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={'Edit Concept'} entityId={entityId} />
      <SidePageV2.NewBody loading={loading}>
        <Section title={'Concept'}>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <FormField.TextField
                label={'Name'}
                name={CONCEPT.NAME}
                helper={'1-3 words, 120 characters maximum'}
                required={true}
                padding={0}
              />
            </Grid>
            <Grid item xs={8}>
              <FormField.MultiSelect
                label={'Applications'}
                name={CONCEPT.APPLICATIONS}
                source={loadApplications}
                required={true}
                padding={0}
              />
            </Grid>
            {isAllowConceptFamilies ? (
              <Grid item xs={4}>
                <ConceptFamiliesMultiSelect />
              </Grid>
            ) : null}
            {isAllowPromoCodes ? (
              <Grid item xs={4}>
                <FormField.Select
                  label={'Promo Code'}
                  name={CONCEPT.PROMO_CODE}
                  source={loadPromoCodes}
                  depends={[CONCEPT.APPLICATIONS]}
                  withDeselectButton={true}
                  normalizeValue={normalizeSelectValue}
                  padding={0}
                />
              </Grid>
            ) : null}
            <Grid item xs={4}>
              <FormField.Checkbox
                label={'AI Generated'}
                name={CONCEPT.AI_GENERATED}
                padding={0}
              />
            </Grid>
            <Grid item xs={12}>
              <TagsMultiSelect
                source={loadTags}
                name={CONCEPT.TAGS}
                withAddTags={isAllowAddTags}
              />
            </Grid>
            {isAllowJiraTask ? (
              <Grid item xs={8}>
                <FormField.TextField
                  label={'Jira task URL'}
                  name={CONCEPT.JIRA_TASK}
                  padding={0}
                />
              </Grid>
            ) : null}
            {isAllowRequestedBy ? (
              <Grid item xs={4}>
                <FormField.Select
                  label={'Requested by'}
                  name={CONCEPT.REQUESTED_BY}
                  source={loadRequestedBy}
                  required={true}
                  padding={0}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <FormField.TextField
                label={'Description'}
                name={CONCEPT.DESCRIPTION}
                multiline={true}
                padding={0}
              />
            </Grid>
          </Grid>
        </Section>
        {isAllowAttachments ? (
          <Section title={'Attachments'}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormField.AttachmentField
                  name={CONCEPT.ATTACHMENTS}
                  label={'Select files'}
                  tooltip={'Only pdf or jpg (jpeg) files are allowed'}
                  accept={'.pdf, .jpg, .jpeg'}
                  onUpload={uploadAttachments}
                  variant={'outlined'}
                  multiple={true}
                  padding={0}
                />
              </Grid>
            </Grid>
          </Section>
        ) : null}
        {isAllowChangeConceptStatus ? (
          <Section title={'Status'}>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <StatusSchedulesField
                  name={CONCEPT.STATE_SCHEDULES}
                  readOnlyCurrent={true}
                  padding={0}
                />
              </Grid>
            </Grid>
          </Section>
        ) : null}
        {promoCodeDetails?.expirationDate ? (
          <Alert
            severity={'info'}
            title={`Promo Code expiration date - ${dateFormat(promoCodeDetails.expirationDate)}`}
          >
            Note: This creative will be archived based on the promo code's end
            date. If a different end date is set, the earliest date will take
            priority
          </Alert>
        ) : null}
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {children}
    </SidePageV2>
  );
});

export default EditConceptForm;
